import { css } from '@emotion/react';
import { Box, Stack } from '@mui/material';
import { useAtom, useAtomValue } from 'jotai';
import { useState } from 'react';
import { FieldPath, FormProvider, useForm } from 'react-hook-form';
import { GenericFallbacksWrapper } from '../../../components/Fallback/GenericFallbacksWrapper';
import { FormFactoryNoLayout } from '../../../components/Form/FormFactory';
import { CompanyListContainerBreakpoints, ContainerName } from '../../../constants/styles';
import { ICompanyDataModel } from '../../../data-models/company.data-model';
import { field2ToFormField, FieldEntity, FormOptions } from '../../../data-models/field2.data-model';
import { IListDataModel } from '../../../data-models/list.data-model';
import { IPersonDataModel } from '../../../data-models/person.data-model';
import { PanelPreferences } from '../../../data-models/preferences-v3.data-model';
import { useContainerDimensions } from '../../../hooks/useContainerDimensions';
import { customFieldsByEntityAtom } from '../../../services/state/AdminPanel/CustomFieldsStateJ';
import { ICompanyViewModel } from '../../../view-models/company.view-model';
import { createFormFromFieldsArray, IFormField } from '../../../view-models/form.view-model';
import { selectedListOverviewFieldsOrgState } from '../../AdminPanel/DataDisplay/DataDisplayState';
import { usePanelPagination } from '../../CompanyProfiles2/Summary/CompanyDetailsPanel';
import {
  fromCompanyDataModel,
  toCompanyDataModel,
} from '../../CompanyProfiles2/Summary/DigestPanel.view-model';
import { Panel } from '../../CompanyProfiles2/Summary/Panel';
import { transformCompanyFormFieldForLists } from '../../CompanyProfiles2/Summary/useCompanyListsFields';
import { useDebounceCompanyUpdates } from '../../CompanyProfiles2/Summary/useDebounceCompanyUpdates';
import { usePeopleBreakdown } from '../../CompanyProfiles2/utils/usePeopleBreakdown';
import { useCompanyFields } from '../../Finance2/Forms/CompanyFormUtils';
import { ListContainerRef } from '../Companies/state/ListsUiState';
import { NoDataMessage2 } from '../components/Messages/HelpMessage';
import { PersonData } from '../Summary/components/People/PersonData';
import { PersonDrawer } from './People/PersonDrawer';

const maxFieldsPerPage = 8;

const FieldsWrapperCss = css`
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  @container ${ContainerName.companyLists} (max-width: ${CompanyListContainerBreakpoints.small}px) {
    grid-template-columns: minmax(0, 1fr);
  }
  grid-auto-rows: min-content;
  margin: 0 0.75rem;
`;

const ListPanelStyles = {
  height: 'unset',
};

const FieldPanelStyles = {
  ...ListPanelStyles,
  minHeight: '24rem',
};

interface CompanyDetailsProps {
  company: ICompanyDataModel;
  activeList: IListDataModel;
}

interface ListCompanyPanelProps extends Pick<CompanyDetailsProps, 'activeList'> {
  company: ICompanyViewModel;
  onChange: (data: Partial<ICompanyViewModel>) => void;
  preferences: PanelPreferences<FieldPath<ICompanyViewModel>>;
}

export function CompanyDetails2(props: CompanyDetailsProps) {
  const [selectedFields] = useAtom(selectedListOverviewFieldsOrgState);
  const companyCustomFieldsPref = { selectedFields: [] };

  const { company: companyData } = props;
  const company = fromCompanyDataModel(companyData);
  const updateCompany = useDebounceCompanyUpdates(companyData.id);
  const formMethods = useForm({
    values: company,
  });

  function onChange(data: Partial<ICompanyViewModel>) {
    updateCompany(toCompanyDataModel(data));
  }
  const maxLeadersPerPage = useMaxLeadersPerPage();
  return (
    <FormProvider {...formMethods}>
      <Box display='grid' gap={'1rem'}>
        <CompanyOverviewPanel
          activeList={props.activeList}
          company={company}
          onChange={onChange}
          preferences={{ selectedFields }}
        />

        <GenericFallbacksWrapper>
          <CompanyLeadershipPanel
            company={companyData}
            maxItemsPerPage={maxLeadersPerPage}
            // the key prevents the panel becoming empty on resize (wider screen -> less pages -> empty panel if last page was selected)
            key={maxLeadersPerPage}
          />
        </GenericFallbacksWrapper>

        <CompanyCustomFieldsPanel
          activeList={props.activeList}
          company={company}
          onChange={onChange}
          preferences={companyCustomFieldsPref}
        />
      </Box>
    </FormProvider>
  );
}

function CompanyOverviewPanel(props: ListCompanyPanelProps) {
  const { visibleFields, paginationConfig } = usePanelPagination({
    fields: props.preferences.selectedFields ?? [],
    itemsPerPage: maxFieldsPerPage,
  });
  return (
    <Panel title='Overview' style={FieldPanelStyles} paginationConfig={paginationConfig}>
      <div>
        <div css={FieldsWrapperCss}>
          <CompanyFields company={props.company} onChange={props.onChange} fields={visibleFields} />
        </div>
      </div>
    </Panel>
  );
}

function CompanyFields(props: {
  company: ICompanyViewModel;
  onChange: (data: Partial<ICompanyViewModel>) => void;
  fields: FieldPath<ICompanyViewModel>[];
}) {
  const { company, onChange, fields } = props;
  const form = useCompanyDetailsViewModel(company, fields);

  return <FormFactoryNoLayout form={form} onChange={onChange} changeDebounce={500} />;
}

function useCompanyDetailsViewModel(
  company: ICompanyViewModel,
  selectedFields: FieldPath<ICompanyViewModel>[]
) {
  const editableFields = new Set(['assignee', 'sectorId']);
  const totalRaisedField = company.companyType === 'portfolio' ? 'raisedToDate' : 'totalPIC';
  const fields = selectedFields.map((f) => (f === '_viewModel.totalRaised' ? totalRaisedField : f));
  const formFields = useCompanyFields({
    fields,
    variant: 'form-inline',
    customFields: 'none',
  }).map(transformCompanyFormFieldForLists);

  formFields.forEach((field) => {
    field.disabled = !editableFields.has(field.key);
  });

  return createFormFromFieldsArray<ICompanyViewModel>(formFields, { variant: 'form-inline' });
}

function CompanyCustomFieldsPanel(props: ListCompanyPanelProps) {
  const fields =
    useCompanyListCustomFieldsViewModel(/* TODO: create set from (props.preferences.selectedFields) MAGGIE-8938 */);
  const { visibleFields, paginationConfig } = usePanelPagination({
    fields: fields ?? [],
    itemsPerPage: maxFieldsPerPage,
  });
  const formMethods = useForm({
    values: props.company,
  });

  if (!fields) {
    return null;
  }

  const form = createFormFromFieldsArray<ICompanyViewModel>(visibleFields, { variant: 'form-inline' });

  return (
    <Panel title='Details' style={FieldPanelStyles} paginationConfig={paginationConfig}>
      <FormProvider {...formMethods}>
        <div css={FieldsWrapperCss}>
          <FormFactoryNoLayout form={form} onChange={props.onChange} />
        </div>
      </FormProvider>
    </Panel>
  );
}

export function useCompanyListCustomFieldsViewModel(selectedFields?: Set<string>) {
  const customFields = useAtomValue(customFieldsByEntityAtom);
  const listCompanyCustomFields = (customFields.get(FieldEntity.company) ?? []).reduce((acc, field) => {
    if (
      field.systemMeta.formRefs.includes(FormOptions.companyLists) &&
      (!selectedFields || selectedFields.has(field.entityField))
    ) {
      acc.push(field2ToFormField(field));
    }
    return acc;
  }, [] as IFormField<unknown>[]);

  if (listCompanyCustomFields.length === 0) {
    return null;
  }

  return listCompanyCustomFields;
}

export function CompanyLeadershipPanel({
  company,
  maxItemsPerPage,
}: {
  company: ICompanyDataModel;
  maxItemsPerPage: number;
}) {
  const { visiblePeople: people } = usePeopleBreakdown(company.id);
  const { paginationConfig, visibleFields } = usePanelPagination({
    fields: people ?? [],
    itemsPerPage: maxItemsPerPage,
  });

  return (
    <Panel title='Leadership' paginationConfig={paginationConfig} style={ListPanelStyles}>
      <Stack display='grid' gridAutoFlow={'column'} gap={'1rem'}>
        {visibleFields.length ? (
          <People people={visibleFields} company={company} />
        ) : (
          <div style={{ width: '100%' }}>
            <NoDataMessage2 />
          </div>
        )}
      </Stack>
    </Panel>
  );
}

function useMaxLeadersPerPage() {
  const listContainerRef = useAtomValue(ListContainerRef);
  const dimensions = useContainerDimensions(listContainerRef);

  if (dimensions.width <= CompanyListContainerBreakpoints.medium) {
    return 2;
  }
  return 3;
}

function People({ people, company }: { people: IPersonDataModel[]; company: ICompanyDataModel }) {
  const [selectedPerson, setSelectedPerson] = useState<IPersonDataModel | null>(null);

  return (
    <>
      <PersonDrawer
        anchor={'right'}
        open={Boolean(selectedPerson)}
        onClose={() => setSelectedPerson(null)}
        personData={selectedPerson}
        company={company}
      />

      {people.map((person) => {
        return (
          <PersonData
            person={person}
            companyId={company.id}
            onClick={() => setSelectedPerson(person)}
            key={person.id}
          />
        );
      })}
    </>
  );
}
